import {
  Box,
  colors,
  ContentText,
  Link,
  Toggle,
  fontWeights,
  LazyIcon,
  Button,
  IconCheck,
  LazyIconProps,
} from '@fortum/elemental-ui';
import FormattedEnhancedMessage from '@utils/intl-extenders/FormattedEnhancedMessage';
import { getProperPriceString, hasDiscount } from '@utils/prices';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Addon } from 'types/api';

interface ItemProps {
  addon: Addon;
  active?: boolean;
  onReadMoreClick?: () => void;
  onSwitchChange?: () => void;
  readOnly?: boolean;
  disabled?: boolean;
  hideShortDescription?: boolean;
}

const ReadMoreLink = ({ onClick }: { onClick?: () => void }) => (
  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  <Link size="xs" color={colors.ivyGreen600} onClick={onClick}>
    <FormattedEnhancedMessage i18nKey="common.readMore" />
  </Link>
);

const VasCard: React.FC<ItemProps> = ({
  addon,
  active,
  onReadMoreClick,
  onSwitchChange,
  readOnly,
  hideShortDescription = false,
  disabled = false,
}) => {
  const [value, setValue] = useState(active);
  const { i18n, t } = useTranslation();
  const icon = i18n.exists(`addons.${addon.tariffId}.icon`)
    ? (t(`addons.${addon.tariffId}.icon`) as LazyIconProps['iconId'])
    : null;
  const extraPriceText = i18n.exists(`addons.${addon.tariffId}.extraPriceText`)
    ? t(`addons.${addon.tariffId}.extraPriceText`)
    : null;

  const basePrice = getProperPriceString(addon.price, t);
  const priceAfterDiscount = getProperPriceString(addon.price, t, true);

  const hasDiscountValue = hasDiscount(addon.price);
  const priceObject = addon.price.fee;

  const discountTo =
    hasDiscountValue && priceObject?.discountTo
      ? dayjs(priceObject.discountTo).format('D.MM.YYYY')
      : null;

  const discountFrom =
    hasDiscountValue && priceObject?.discountFrom
      ? dayjs(priceObject.discountFrom).format('D.MM.YYYY')
      : null;

  return (
    <Box padding="1rem" border={`1px solid ${colors.fogGrey}`} margin="1rem 0">
      <Box display="flex" alignItems="center">
        {icon ? (
          <LazyIcon iconId={icon} color={colors.ivyGreen600} size="3rem" />
        ) : null}
        <Box marginLeft={icon ? '1rem' : 0}>
          <ContentText size="xl" color={colors.inkGrey}>
            {t(`addons.${addon.tariffId}.title`)}
          </ContentText>
        </Box>
      </Box>
      {!hideShortDescription && (
        <ContentText
          marginTop="1rem"
          color={colors.inkGrey}
          fontWeight={fontWeights.light}
          lineHeight={1.7}
        >
          {t(
            `addons.${addon.tariffId}.shortDescription${active ? 'Chosen' : ''}`
          )}
        </ContentText>
      )}
      <ReadMoreLink onClick={onReadMoreClick} />
      <Box
        display="flex"
        marginTop="1rem"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          paddingRight="1rem"
        >
          <ContentText color={colors.ivyGreen600} size="l">
            {hasDiscountValue ? priceAfterDiscount : basePrice}
          </ContentText>
          {hasDiscountValue && (
            <>
              <ContentText
                style={{ textDecorationLine: 'line-through' }}
                color={colors.sonicGrey}
                size="l"
              >
                {basePrice}
              </ContentText>
              <ContentText size="xs" color={colors.ivyGreen600}>
                {discountTo
                  ? t('invoice.discountValidity', {
                      to: discountTo,
                      from: discountFrom,
                    })
                  : t('invoice.discountNoValidTo')}
              </ContentText>
            </>
          )}
          {extraPriceText && (
            <ContentText size="xs" color={colors.ivyGreen600}>
              {extraPriceText}
            </ContentText>
          )}
        </Box>
        {readOnly ? (
          <Button
            leftIcon={<IconCheck marginRight="-4px" />}
            color={colors.snowWhite}
            textColor={colors.snowWhite}
            backgroundColor={colors.fogGrey}
            borderRadius="1rem"
            fontSize="1rem"
            padding="6.5px 16px"
            height="35px"
          >
            {t('common.active')}
          </Button>
        ) : (
          <Toggle
            checked={value}
            name={addon.tariffId}
            height="2rem"
            disabled={disabled}
            onChange={() => {
              if (!onSwitchChange) return;
              onSwitchChange();
              setValue((prev) => !prev);
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default VasCard;
